<template>
  <div>
    <div>
      <BaseInputText
        v-model="text"
        :aria-activedescendant="
          itemIndex >= 0 ? `${listboxId}-option-${itemIndex}` : undefined
        "
        :aria-controls="shouldShowDropdown ? listboxId : undefined"
        :aria-expanded="dropdownVisible"
        aria-haspopup="listbox"
        autocomplete="off"
        :class="{ disabled: disabled }"
        :clearable="clearable"
        data-testid="input"
        :disabled="disabled"
        new-design
        :placeholder="placeholder"
        role="combobox"
        @blur="onTextBlur"
        @clear="onTextClear"
        @click="onTextFocus"
        @focus="onTextFocus"
        @keydown.down.stop="incrementSearchIndex"
        @keydown.enter.prevent
        @keydown.enter.stop="onKeyEnter"
        @keydown.tab="onKeyTab"
        @keydown.up.stop="decrementSearchIndex"
      >
        <template #prepend>
          <BaseIcon
            class="tw:text-xl"
            icon="search"
          />
        </template>
        <template #append>
          <div
            v-if="loading"
            class="tw:px-1"
          >
            <BaseSpinner class="tw:border-denim-700 tw:size-4" />
          </div>
        </template>
      </BaseInputText>
    </div>
    <BasePopper
      v-if="shouldShowDropdown"
      v-model="dropdownVisible"
      data-testid="popper"
      placement="bottom-start"
      same-width
    >
      <div
        class="tw:rounded-sm tw:border tw:border-solid tw:border-denim-300 tw:bg-white"
        data-testid="dropdown"
        @mousedown="onDropdownMousedown"
        @mouseup="allowDropdownClose"
      >
        <slot name="beforeOptions" />
        <div v-if="!hasItems">
          <slot
            v-if="!items.length && query.length >= minimumCharacters"
            name="noResults"
            :query="query"
          >
            <div class="tw:p-2">
              <i18n-t
                class="no-results"
                keypath="input_autocomplete.no_results_found_for"
                scope="global"
                tag="div"
              >
                <template #search>
                  <em>{{ query }}</em>
                </template>
              </i18n-t>
            </div>
          </slot>
          <div
            v-else-if="query.length > 0 && query.length < minimumCharacters"
            class="tw:p-2"
          >
            {{
              getLocaleText("input_autocomplete.type_at_least", {
                minimumCharacters,
              })
            }}
          </div>
        </div>
        <div
          v-else
          :id="listboxId"
          class="list tw:max-h-[260px] tw:overflow-auto"
          data-testid="list"
          role="listbox"
        >
          <QInfiniteScroll
            ref="infiniteScroll"
            data-testid="scroll"
            :offset="pageSize"
            scroll-target=".list"
            @load="loadMoreItems"
          >
            <div
              v-for="(item, index) in items"
              :id="`${listboxId}-option-${index}`"
              :key="index"
              ref="itemElements"
              :aria-selected="itemIndex === index"
              class="tw:cursor-pointer tw:select-none tw:p-2 tw:hover:bg-denim-100"
              :class="{
                'tw:bg-denim-100': itemIndex === index,
                'tw:outline tw:outline-primary tw:-outline-offset-2':
                  itemIndex === index,
              }"
              data-testid="item"
              role="option"
              @click="selectItem(item)"
              @mousedown="preventDropdownClose"
              @mouseup="allowDropdownClose"
            >
              <slot
                name="option"
                :option="item"
              >
                <BaseEllipseContent>
                  {{ item.name }}
                </BaseEllipseContent>
              </slot>
            </div>

            <template #loading>
              <div class="tw:pa-2 tw:flex tw:justify-center">
                <QSpinnerDots size="2em" />
              </div>
            </template>
          </QInfiniteScroll>
        </div>
        <slot name="afterOptions" />
      </div>
    </BasePopper>
  </div>
</template>

<script lang="ts">
export interface InputAutocompleteResponseItem {
  id: number | string;
  name: string;
}
</script>

<script setup lang="ts" generic="T extends InputAutocompleteResponseItem">
import { debounce } from "lodash-es";
import { QInfiniteScroll, QSpinnerDots } from "quasar";
import {
  computed,
  nextTick,
  onUnmounted,
  type Ref,
  ref,
  useId,
  watch,
} from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseEllipseContent,
  BaseIcon,
  BaseInputText,
  BasePopper,
  BaseSpinner,
} from "shared/components/base";
import { type Maybe } from "shared/types";

export interface InputAutocompleteResponse<T> {
  items: T[];
  pages: number;
}

export interface InputAutocompleteProps<T> {
  disabled?: boolean;
  disableSearchOnFocus?: boolean;
  displayText?: string;
  filter: (index: number) => Promise<InputAutocompleteResponse<T>>;
  identifierKey?: keyof T & string;
  keepExpanded?: boolean;
  minimumCharacters?: number;
  pageSize?: number;
  placeholder?: string;
}

const props = withDefaults(defineProps<InputAutocompleteProps<T>>(), {
  displayText: "",
  identifierKey: "id",
  minimumCharacters: 3,
  pageSize: 40,
  placeholder: "",
});

const modelValue = defineModel<T[keyof T & string]>();

const emit = defineEmits<{
  item: [value: Maybe<T>];
  query: [value: string];
  visible: [value: boolean];
}>();

const listboxId = useId();

const loading = ref<boolean>(false);
const query = ref<string>("");
const items: Ref<T[]> = ref([]);
const dropdownVisible = ref<boolean>(false);
const allowDropdownToClose = ref<boolean>(true);
const itemIndex = ref<number>(0);
const infiniteScroll = ref<InstanceType<typeof QInfiniteScroll> | undefined>();
const itemElements = ref<HTMLDivElement[]>([]);
const page = ref<number>(1);
const selectedItem: Ref<T | undefined> = ref(undefined);

const clearable = computed<boolean>(() =>
  Boolean(!loading.value && modelValue.value && !query.value)
);

function getIdentifierKeyValue(item: T) {
  return item[props.identifierKey];
}

const text = computed<string>({
  get() {
    if (dropdownVisible.value) return query.value;

    if (props.displayText) return props.displayText;

    if (selectedItem.value) return selectedItem.value.name;

    if (modelValue.value) return String(modelValue.value);

    return "";
  },
  set(value) {
    query.value = value;
  },
});

const hasItems = computed<boolean>(() => Boolean(items.value.length));

const shouldShowDropdown = computed<boolean>(() =>
  Boolean(dropdownVisible.value && (query.value || hasItems.value))
);

function showDropdown(): void {
  dropdownVisible.value = true;
}

async function loadItems(index: number): Promise<void> {
  loading.value = true;

  try {
    const result = await props.filter(index);

    if (page.value >= result.pages) {
      infiniteScroll.value?.stop();
    }

    items.value.push(...result.items);
    page.value += 1;
  } catch {
    // do nothing
  } finally {
    loading.value = false;
  }
}

const debouncedSearch = debounce(() => {
  loadItems(0);
}, 300);

function startSearch(): void {
  page.value = 1;
  items.value = [];
  loading.value = true;
  debouncedSearch();
}

const loadMoreItems: QInfiniteScroll["onLoad"] = async (
  index: number,
  done: () => void
) => {
  if (loading.value) {
    done();

    return;
  }

  await loadItems(index);

  done();
};

function hideDropdown(): void {
  dropdownVisible.value = false;
  itemIndex.value = -1;
  query.value = "";
  page.value = 1;
}

function selectItem(item: T): void {
  emit("item", item);

  if (props.keepExpanded) {
    // Multi-select (chip) mode: clear input ready for next search
    modelValue.value = undefined;
    query.value = "";
  } else {
    // Single-select mode: persist selection in modelValue and close
    modelValue.value = getIdentifierKeyValue(item);
    selectedItem.value = item;
    hideDropdown();
  }
}

function onTextBlur(): void {
  if (!allowDropdownToClose.value) return;

  hideDropdown();
}

function onTextFocus(): void {
  query.value = "";
  showDropdown();

  if (!props.disableSearchOnFocus) {
    startSearch();
  }
}

function onTextClear(): void {
  modelValue.value = undefined;
  selectedItem.value = undefined;
  emit("item", undefined);
}

type ItemElement = HTMLDivElement & {
  scrollIntoViewIfNeeded?: (centerIfNeeded: boolean) => void;
};

function scrollOptionIntoView(): void {
  nextTick(() => {
    const element: ItemElement = itemElements.value[itemIndex.value];

    if (!element) return;

    if (element.scrollIntoViewIfNeeded !== undefined) {
      element.scrollIntoViewIfNeeded(false);
    } else if (element.scrollIntoView !== undefined) {
      element.scrollIntoView({ block: "nearest" });
    }
  });
}

function decrementSearchIndex(): void {
  if (itemIndex.value - 1 >= 0) {
    itemIndex.value -= 1;
    scrollOptionIntoView();
  }
}

function incrementSearchIndex(): void {
  if (itemIndex.value + 1 < items.value.length) {
    itemIndex.value += 1;
    scrollOptionIntoView();
  }
}

function onKeyEnter(): void {
  if (itemIndex.value >= 0) {
    const item = items.value[itemIndex.value];
    if (item) selectItem(item);
  }

  hideDropdown();
}

function onKeyTab(event: KeyboardEvent): void {
  if (event.shiftKey || !dropdownVisible.value || !hasItems.value) return;
  // At last item let Tab propagate naturally — focus leaves input, blur closes dropdown
  if (itemIndex.value >= items.value.length - 1) return;

  event.preventDefault();
  event.stopPropagation();
  itemIndex.value += 1;
  scrollOptionIntoView();
}

function onDropdownMousedown(event: MouseEvent): void {
  if (event.target instanceof HTMLInputElement) {
    // Allow focus to move into inputs inside the dropdown (e.g. custom label fields),
    // but prevent the search input's blur from closing the dropdown.
    allowDropdownToClose.value = false;
  } else {
    // Prevent focus from leaving the search input when clicking non-input elements.
    event.preventDefault();
  }
}

function preventDropdownClose(): void {
  allowDropdownToClose.value = false;
}

function allowDropdownClose(): void {
  allowDropdownToClose.value = true;
}

watch(query, () => {
  emit("query", query.value);

  if (query.value.length >= props.minimumCharacters) {
    startSearch();
    showDropdown();
  } else {
    page.value = 1;
    items.value = [];
    itemIndex.value = -1;
  }
});

watch(dropdownVisible, () => {
  emit("visible", dropdownVisible.value);
});

defineExpose({ search: startSearch });

onUnmounted(() => {
  debouncedSearch.cancel();
});
</script>
